import { BaseEffect } from '../BaseEffect.js';

/**
 * Heat distortion effect implementation
 * Creates a dynamic heat wave effect with variable intensity and movement
 */
export class HeatDistortionEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            intensity: 0.02,        // Strength of the distortion
            speed: 1.0,            // Speed of the heat wave animation
            scale: 4.0,            // Scale of the distortion pattern
            aspectCorrection: 1.0,  // Correction for non-square aspect ratios
            refraction: 0.4,       // Amount of light refraction
            heatColorR: 1.0,       // Heat color tint R (subtle orange/red glow)
            heatColorG: 0.6,       // Heat color tint G
            heatColorB: 0.3,       // Heat color tint B
            colorIntensity: 0.1,   // Intensity of the heat color
            animate: true          // Whether to animate the effect
        };

        super('heat-distortion', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_intensity;
            uniform float u_speed;
            uniform float u_scale;
            uniform float u_aspectCorrection;
            uniform float u_refraction;
            uniform vec3 u_heatColor;
            uniform float u_colorIntensity;
            
            varying vec2 v_texcoord;

            // Improved noise function
            vec2 hash2(vec2 p) {
                vec2 k = vec2(0.3183099, 0.3678794);
                p = p * k + k.yx;
                return -1.0 + 2.0 * fract(16.0 * k * fract(p.x * p.y * (p.x + p.y)));
            }

            // Gradient noise
            float gnoise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                
                vec2 u = f * f * (3.0 - 2.0 * f);

                // Get four corner gradients
                vec2 ga = hash2(i + vec2(0.0, 0.0));
                vec2 gb = hash2(i + vec2(1.0, 0.0));
                vec2 gc = hash2(i + vec2(0.0, 1.0));
                vec2 gd = hash2(i + vec2(1.0, 1.0));
                
                // Calculate dot products with gradients
                float va = dot(ga, f - vec2(0.0, 0.0));
                float vb = dot(gb, f - vec2(1.0, 0.0));
                float vc = dot(gc, f - vec2(0.0, 1.0));
                float vd = dot(gd, f - vec2(1.0, 1.0));
                
                // Interpolate
                return mix(
                    mix(va, vb, u.x),
                    mix(vc, vd, u.x),
                    u.y
                );
            }

            // Fractal Brownian Motion
            vec2 fbm(vec2 p) {
                vec2 value = vec2(0.0);
                float amplitude = 0.5;
                float frequency = 1.0;
                
                // Add multiple layers of noise
                for(int i = 0; i < 4; i++) {
                    value += vec2(
                        gnoise(p * frequency + value.yx),
                        gnoise(p * frequency + 1234.5 + value.yx)
                    ) * amplitude;
                    frequency *= 2.0;
                    amplitude *= 0.5;
                }
                
                return value;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                // Adjust UV coordinates for aspect ratio
                vec2 scaledUV = uv * u_scale;
                scaledUV.x *= aspect * u_aspectCorrection;
                
                // Create time-based movement
                float t = u_time * u_speed;
                vec2 movement = vec2(t * 0.3, t * 0.2);
                
                // Generate distortion vector using FBM
                vec2 distortion = fbm(scaledUV + movement);
                
                // Add secondary motion layer
                distortion += fbm(scaledUV * 2.0 - movement * 0.5) * 0.5;
                
                // Apply intensity and create final UV offset
                vec2 finalUV = uv + distortion * u_intensity;
                
                // Sample the texture with distortion
                vec4 color = texture2D(u_image, finalUV);
                
                // Calculate heat intensity based on distortion magnitude
                float heatIntensity = length(distortion) * u_refraction;
                
                // Add subtle heat color glow
                vec3 heatGlow = u_heatColor * heatIntensity * u_colorIntensity;
                color.rgb += heatGlow;
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_intensity', { type: '1f' });
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_scale', { type: '1f' });
        this.addUniform('u_aspectCorrection', { type: '1f' });
        this.addUniform('u_refraction', { type: '1f' });
        this.addUniform('u_heatColor', { type: '3fv' });
        this.addUniform('u_colorIntensity', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_intensity: { value: this.params.intensity, type: '1f' },
            u_speed: { value: this.params.speed, type: '1f' },
            u_scale: { value: this.params.scale, type: '1f' },
            u_aspectCorrection: { value: this.params.aspectCorrection, type: '1f' },
            u_refraction: { value: this.params.refraction, type: '1f' },
            u_heatColor: { 
                value: [this.params.heatColorR, this.params.heatColorG, this.params.heatColorB],
                type: '3fv'
            },
            u_colorIntensity: { value: this.params.colorIntensity, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            intensity: {
                min: 0,
                max: 0.1,
                step: 0.001,
                label: 'Distortion Intensity'
            },
            speed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            scale: {
                min: 1,
                max: 10,
                step: 0.1,
                label: 'Pattern Scale'
            },
            aspectCorrection: {
                min: 0.1,
                max: 2,
                step: 0.1,
                label: 'Aspect Correction'
            },
            refraction: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Refraction'
            },
            heatColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Heat Color R'
            },
            heatColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Heat Color G'
            },
            heatColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Heat Color B'
            },
            colorIntensity: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Color Intensity'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 